import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useIonRouter } from "@ionic/react";

import { useGameContext } from "@/contexts/game-context";
import { useAppContext } from "@/contexts/app-context";
import { Vocabulary } from "@/components/gameplay/vocabulary";
import { Reward } from "@/components/gameplay/reward";
import { VideoPlayer } from "@/components/gameplay/video-player";
import { AudioPlayer } from "@/components/gameplay/audio-player";
import { InputButton } from "@/components/gameplay/inputs";
import SongType from "@/types/song-type";
import VocabularyType from "@/types/vocabulary-type";
import StageType from "@/types/stage-type";
import makeYourOwnSongTabImage from "@/assets/make-your-own-song-tab.png";

export const StageDisplay = () => {
  const { state, eventHandlers } = useGameContext();
  const { phase, stage, vocabulary, videoUrl, videoStartTime, controlVideoUrl, instrumentalAudioUrl, audioUrl, playing, song } = state;
  const { handleEnded, handleAudioEnded, handleTimeUpdate, handleVocabularyCueVideoTimeUpdate } = eventHandlers;
  const [debugging, setDebugging] = useState(false);
  const instrumentalAudioRef = useRef<HTMLAudioElement>(null);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === "d") {
      setDebugging((debugging) => !debugging);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyUp]);

  const showVideoPlayer = ![
    "pre_assessment_prompt",
    "post_assessment_prompt",
  ].includes(phase);

  const showVocabulary =
    [
      "pre_assessment_prompt",
      "post_assessment_prompt",
      "playback",
      "input",
      "audio_hint_playback",
    ].includes(phase) && vocabulary;

  const showReward = phase === "reward";

  return (
    <div className="relative flex flex-col gap-4 items-center justify-between grow p-4">
      <div className="w-full flex gap-4 items-center justify-between">
        {showVideoPlayer && (
          <VideoPlayer
            src={videoUrl}
            startTime={videoStartTime}
            className="md:absolute md:top-6 sm:left-6 rounded-full w-1/4 max-w-36 aspect-square ring-2 ring-gray-400 object-cover object-center opacity-75"
            playsInline
            playing={playing}
            onEnded={handleEnded}
            onTimeUpdate={
              controlVideoUrl 
                ? handleVocabularyCueVideoTimeUpdate // For custom songs' Sing and Instrument stage, monitor for 3-second switch
                : handleTimeUpdate // Normal case
            }
          />
        )}

        <div className="grow flex justify-center md:hidden">
          {showVocabulary && (
            <h2 className="text-4xl text-center">{vocabulary.name}</h2>
          )}
        </div>
      </div>

      {/* Hidden control video for timing image transitions - always muted */}
      {controlVideoUrl && (
        <VideoPlayer
          src={controlVideoUrl}
          className="absolute invisible"
          playsInline
          playing={playing}
          onTimeUpdate={handleTimeUpdate}
          muted={true}
        />
      )}

      {/* Instrumental audio for "Sing and Instrument" stage */}
      {instrumentalAudioUrl && (
        <AudioPlayer
          ref={instrumentalAudioRef}
          src={instrumentalAudioUrl}
          loop
          playing={playing} // Pass playing prop to AudioPlayer
          className="absolute"
        />
      )}

      {audioUrl && (
        <AudioPlayer
          src={audioUrl}
          autoPlay
          onEnded={handleAudioEnded}
          className="absolute"
        />
      )}

      <AnimatePresence mode="wait">
        {(showVocabulary || showReward) && (
          <motion.div
            key={showVocabulary ? `vocabulary-${vocabulary?.id}` : `reward-${stage?.id}`}
            className="grow transition-opacity flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {showVocabulary && (
              <>
                <Vocabulary key={vocabulary?.id} vocabulary={vocabulary} />
                <h2 className="text-4xl text-center hidden md:block pt-4">
                  {vocabulary.name}
                </h2>
              </>
            )}

            {showReward && <Reward key={stage?.id} />}
          </motion.div>
        )}
      </AnimatePresence>

      {process.env.NODE_ENV === "development" && debugging && (
        <div className="absolute right-0 top-0 bottom-0 w-[400px] bg-gray-300 p-2 overflow-auto">
          <h3 className="pb-4 font-semibold">DEBUGGER</h3>
          <ul>
            {Object.entries(state).map(([key, value]) => (
              <li key={key} className="whitespace-nowrap">
                <strong>{key}</strong>:{" "}
                <span>
                  {["string", "number", "boolean"].includes(typeof value)
                    ? value.toString()
                    : value == null
                      ? "null"
                      : JSON.stringify(
                          {
                            id: (value as SongType | VocabularyType | StageType)
                              ?.id,
                            name: (
                              value as SongType | VocabularyType | StageType
                            )?.name,
                          },
                          null,
                          2,
                        )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const StageInput = () => {
  const {
    state: { phase, stage, assessmentQuestion, song, retryCount },
  } = useGameContext();
  const { state: { currentUser } } = useAppContext();
  const router = useIonRouter();

  const handleExit = () => {
    return router.canGoBack() ? router.goBack() : router.push("/songs");
  };

  const handleMakeYourOwn = () => {
    window.location.href = `/songs/${song.id}?tab=make-your-own`;
  };

  const handleMakeYourOwnFromCustom = () => {
    window.location.href = `/songs/${song.baseSongId}?tab=make-your-own`;
  };

  if (phase === "stage_complete") {
    return (
      <div className="min-h-24 bg-blue-200 grow border-b-2 md:border-r-2 md:border-b-0 border-gray-400 p-2 sm:p-4">
        <div className="flex flex-col gap-2 pb-1">
          <div className="text-lg">Stage Complete</div>

          <div className="flex gap-2">
            <button className="btn btn-primary max-w-40" onClick={handleExit}>
              Back to songs
            </button>

            {song?.isCustom && song?.baseSongId && currentUser?.focusGroup !== 'SS4K' && (
              <button className="btn btn-primary whitespace-nowrap px-2 py-1.5" onClick={handleMakeYourOwnFromCustom}>
                <img
                  src={makeYourOwnSongTabImage}
                  alt="Make Your Own Song"
                  className="h-12 w-auto object-contain"
                />
              </button>
            )}

            {!song?.isCustom && song?.allowCustomization !== false && currentUser?.focusGroup !== 'SS4K' && (
              <button className="btn btn-primary whitespace-nowrap px-2 py-1.5" onClick={handleMakeYourOwn}>
                <img 
                  src={makeYourOwnSongTabImage} 
                  alt="Make Your Own Song" 
                  className="h-12 w-auto object-contain"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const showInput = [
    "pre_assessment_prompt",
    "post_assessment_prompt",
    "input",
    "audio_hint_playback",
  ].includes(phase);

  return (
    <div className="min-h-24 bg-blue-200 grow border-b-2 md:border-r-2 md:border-b-0 border-gray-400 p-2 sm:p-4">
      <AnimatePresence>
        {["pre_assessment_prompt", "post_assessment_prompt"].includes(phase) ? (
          <motion.div
            key="assessment-prompt"
            className="sm:text-lg text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: showInput ? 1 : 0 }}
          >
            <div>
              Ask:{" "}
              <em className="font-semibold">
                {assessmentQuestion?.inputPrompt}
              </em>
            </div>
            <div>How did the child respond?</div>
          </motion.div>
        ) : (
          <motion.div
            key="stage-prompt"
            className="sm:text-lg text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: showInput ? 1 : 0 }}
          >
            {stage?.inputPrompt}
          </motion.div>
        )}

        <motion.div
          key="input-buttons"
          className="flex gap-4 justify-evenly p-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: showInput ? 1 : 0 }}
        >
          {["pre_assessment_prompt", "post_assessment_prompt"].includes(phase)
            ? [...(assessmentQuestion?.inputs || []), "skip_assessment"].map(
                (input, index) => (
                  <div key={`inputs-${index}`} className="w-full">
                    <InputButton kind={input} />
                  </div>
                ),
              )
            : (
                <>
                  {(() => {
                    const filteredInputs = stage?.inputs?.filter((input) => input?.trim()) || [];
                    const shouldShowSkip = song.isCustom && retryCount >= 2 && phase === "input";
                    
                    if (!shouldShowSkip || filteredInputs.length === 0) {
                      // Render normally without skip button
                      return filteredInputs.map((input, index) => (
                        <div key={`input-${input}-${index}`} className="w-full">
                          <InputButton kind={input} disabled={phase !== "input"} />
                        </div>
                      ));
                    }
                    
                    // Insert skip button at the second position (index 1)
                    const [firstInput, ...restInputs] = filteredInputs;
                    return (
                      <>
                        <div key={`input-${firstInput}-0`} className="w-full">
                          <InputButton kind={firstInput} disabled={phase !== "input"} />
                        </div>
                        <div key="skip-stage-button" className="w-full">
                          <InputButton kind="skip_stage" />
                        </div>
                        {restInputs.map((input, index) => (
                          <div key={`input-${input}-${index + 1}`} className="w-full">
                            <InputButton kind={input} disabled={phase !== "input"} />
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </>
              )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

